import { requisicaoAutenticada } from './http';
import type { Colaborador, EmpresaResumo } from './empresas';
import type { LinhaSped } from './operacao';

/**
 * Operações que **alteram** dados.
 *
 * Separadas das leituras de propósito. Numa tela fiscal a diferença entre ler e
 * escrever não é acadêmica: toda função daqui tem consequência que o usuário
 * não desfaz sozinho, e concentrá-las torna óbvio o que precisa de confirmação,
 * de invalidação de cache e de mensagem de erro cuidadosa.
 */

/* ─── Empresa ─────────────────────────────────────────────────────────────── */

export interface DadosDeEmpresa {
  readonly taxpayerId: string;
  readonly legalName: string;
  readonly name?: string;
  readonly emailAddress: string;
  readonly phone?: string;
  readonly addressCity?: string;
  readonly addressStateCode?: string;
  readonly addressStreetName?: string;
  readonly addressStreetNumber?: string;
  readonly addressDistrict?: string;
  readonly addressPostalCode?: string;
  readonly cadIcms?: string;
  readonly credencialSiscred?: string;
}

export async function editarEmpresa(
  businessId: string,
  dados: Partial<Omit<DadosDeEmpresa, 'taxpayerId'>>,
): Promise<EmpresaResumo> {
  return requisicaoAutenticada<EmpresaResumo>(`/business/${businessId}`, {
    metodo: 'PATCH',
    corpo: dados,
  });
}

export async function desativarEmpresa(businessId: string): Promise<void> {
  await requisicaoAutenticada<void>(`/business/${businessId}`, { metodo: 'DELETE' });
}

/* ─── Colaboradores ───────────────────────────────────────────────────────── */

/**
 * RN-311 — o que a API aceita ao vincular um colaborador.
 *
 * `occupation` é o vínculo trabalhista, numa lista fechada (`VINCULOS_TRABALHISTAS`
 * do domínio): a API recusa com 400 qualquer valor fora dela, porque o cronograma
 * escolhe destinatários por esses rótulos. Aceita a lista ou a string já unida
 * por ", ".
 *
 * `senha` é a senha inicial, opcional, que o operador combina com a pessoa: ela
 * **não** vai por e-mail e nunca volta na resposta. O e-mail, quando pedido
 * (`enviarCredenciais`), leva o link para a pessoa criar a senha dela. O nome do
 * campo é o do schema da API (`criarColaboradorSchema.senha`) — mandar outro
 * nome seria ignorado em silêncio pela validação.
 */
export interface DadosDeColaborador {
  readonly fullName: string;
  readonly emailAddress: string;
  readonly taxpayerId: string;
  readonly phone?: string;
  readonly occupation?: string | readonly string[];
  readonly senha?: string;
  readonly gestor: boolean;
  readonly assinaPelaEmpresa: boolean;
  readonly enviarCredenciais: boolean;
}

/**
 * O que aconteceu com o e-mail de credenciais.
 *
 * A tela **precisa** mostrar isso: no legado a falha do envio era engolida e o
 * colaborador ficava cadastrado sem nunca receber a senha (dívida #16). Quem
 * cadastrou via sucesso; a pessoa descobria ligando.
 */
export interface ResultadoCredenciais {
  readonly enviado: boolean;
  /** `false` quando ninguém pediu o e-mail — escolha, não falha. */
  readonly solicitado?: boolean;
  readonly motivo?: string;
}

export interface ColaboradorCriado extends Colaborador {
  readonly credenciais: ResultadoCredenciais;
}

export async function criarColaborador(
  businessId: string,
  dados: DadosDeColaborador,
): Promise<ColaboradorCriado> {
  return requisicaoAutenticada<ColaboradorCriado>(`/business/${businessId}/colaboradores`, {
    metodo: 'POST',
    corpo: dados,
  });
}

export async function editarColaborador(
  businessId: string,
  accountId: string,
  dados: Partial<
    Pick<DadosDeColaborador, 'fullName' | 'phone' | 'occupation' | 'gestor' | 'assinaPelaEmpresa'>
  > & {
    /** Manda o link para a pessoa criar a senha; a atual continua valendo. */
    readonly enviarLinkDeSenha?: boolean;
    /** Só a operação (admin): a senha passa a valer na hora. */
    readonly senha?: string;
  },
): Promise<ColaboradorCriado> {
  return requisicaoAutenticada<ColaboradorCriado>(
    `/business/${businessId}/colaboradores/${accountId}`,
    { metodo: 'PATCH', corpo: dados },
  );
}

export async function desvincularColaborador(businessId: string, accountId: string): Promise<void> {
  await requisicaoAutenticada<void>(`/business/${businessId}/colaboradores/${accountId}`, {
    metodo: 'DELETE',
  });
}

/* ─── Dados bancários ─────────────────────────────────────────────────────── */

export interface DadosBancarios {
  readonly banco?: string;
  readonly agencia?: string;
  readonly conta?: string;
  readonly pix?: string;
  readonly taxpayerId?: string;
}

export async function salvarDadosBancarios(
  businessId: string,
  dados: DadosBancarios,
): Promise<DadosBancarios> {
  return requisicaoAutenticada<DadosBancarios>(`/business/${businessId}/dados-bancarios`, {
    metodo: 'PUT',
    corpo: dados,
  });
}

/* ─── SPED ────────────────────────────────────────────────────────────────── */

export interface ResultadoUploadSped {
  readonly clienteId: string;
  readonly linhas: readonly LinhaSped[];
  readonly avisos: readonly { arquivo: string; codigo: string; mensagem: string }[];
  readonly recusados: readonly { arquivo: string; motivo: string }[];
}

/**
 * RN-130 — Envio de arquivos SPED.
 *
 * O conteúdo vai como **texto**, não base64: o EFD é texto delimitado por `|`, e
 * o schema do backend espera `conteudo` como string. A leitura resolve a
 * codificação do mesmo jeito da calculadora pública — ISO-8859-1 é o que a
 * maioria dos emissores gera, e decodificar como UTF-8 estraga a razão social do
 * registro 0000.
 */
export async function enviarSpeds(
  clienteId: string,
  arquivos: readonly File[],
): Promise<ResultadoUploadSped> {
  const lidos = await Promise.all(
    arquivos.map(async (arquivo) => ({
      nome: arquivo.name,
      conteudo: await lerTextoDoSped(arquivo),
    })),
  );

  return requisicaoAutenticada<ResultadoUploadSped>('/icms/spedfiles', {
    metodo: 'POST',
    corpo: { clienteId, arquivos: lidos },
  });
}

async function lerTextoDoSped(arquivo: File): Promise<string> {
  const bytes = await arquivo.arrayBuffer();
  try {
    // Se decodifica como UTF-8 estrito, era UTF-8. A ordem importa: windows-1252
    // aceita qualquer byte e nunca falharia.
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/* ─── Passivo ─────────────────────────────────────────────────────────────── */

export interface EntradaSimulacaoPassivo {
  readonly valorDivida: number;
  readonly percentualDesagio: number;
  readonly quantidadeParcelas: number;
  readonly taxaJurosMensal: number;
  readonly indice: 'price' | 'sac';
}

export interface ParcelaSimulada {
  readonly numero: number;
  readonly valor: number;
  readonly amortizacao: number;
  readonly juros: number;
  readonly saldoDevedor: number;
}

export interface ResultadoSimulacaoPassivo {
  readonly valorDivida: number;
  readonly valorCreditoComprado: number;
  readonly percentualDesagio: number;
  readonly valorAPagar: number;
  readonly economia: number;
  readonly percentualEconomia: number;
  readonly valorTotal: number;
  readonly parcelas: readonly ParcelaSimulada[];
}

export async function simularPassivo(
  entrada: EntradaSimulacaoPassivo,
): Promise<ResultadoSimulacaoPassivo> {
  return requisicaoAutenticada<ResultadoSimulacaoPassivo>('/passivo/simulacoes', {
    metodo: 'POST',
    corpo: entrada,
  });
}
